import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useModal } from '../App';

const CTASection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { openModal } = useModal();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="contact" className="py-24 relative overflow-hidden">
      {/* Split Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#04101C]" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#00C4CC]/20 via-[#00E08F]/10 to-transparent" />
      </div>

      {/* Animated Glows */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#00C4CC]/20 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[#00E08F]/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00C4CC]/10 border border-[#00C4CC]/30 mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#00E08F] animate-pulse" />
            <span className="text-[#00E08F] text-sm font-medium">Ready to Start?</span>
          </div>

          {/* Headline */}
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-[#F5FDFF] mb-6 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Let&apos;s Build Your{' '}
            <span className="bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent">
              Podcast
            </span>
          </h2>

          {/* Subtext */}
          <p
            className={`text-xl text-[#F5FDFF]/70 mb-10 transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Drop your idea. We&apos;ll handle the rest.
          </p>

          {/* CTA Button */}
          <div
            className={`transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <button 
              onClick={openModal}
              className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] text-[#04101C] font-bold text-lg hover:shadow-[0_0_60px_rgba(0,224,143,0.5)] transition-all duration-300 hover:scale-105 animate-glow"
            >
              <span>Book a Free Discovery Call</span>
              <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform duration-300" />

              {/* Breathing Glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] opacity-30 blur-2xl -z-10 animate-pulse" />
            </button>
          </div>

          {/* Trust Indicators */}
          <div
            className={`mt-12 flex flex-wrap items-center justify-center gap-8 transition-all duration-700 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex items-center gap-2 text-[#F5FDFF]/50">
              <svg className="w-5 h-5 text-[#00E08F]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Free Consultation</span>
            </div>
            <div className="flex items-center gap-2 text-[#F5FDFF]/50">
              <svg className="w-5 h-5 text-[#00E08F]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">No Commitment</span>
            </div>
            <div className="flex items-center gap-2 text-[#F5FDFF]/50">
              <svg className="w-5 h-5 text-[#00E08F]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Launch in 4 Weeks</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 30px rgba(0, 224, 143, 0.3); }
          50% { box-shadow: 0 0 50px rgba(0, 224, 143, 0.5); }
        }
        .animate-glow {
          animation: glow 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CTASection;
